import { useEffect, useState } from 'react';

export interface FetchState<T> {
  data?: T;
  loading: boolean;
  error?: string;
}

/**
 * Minimal data-fetching hook: runs `fn` on mount and when `deps` change.
 * `reload()` re-runs it. (Dependency-free; swap for React Query if needs grow.)
 */
export function useFetch<T>(fn: () => Promise<T>, deps: unknown[] = []): FetchState<T> & { reload: () => void } {
  const [state, setState] = useState<FetchState<T>>({ loading: true });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let active = true;
    setState({ loading: true });
    fn()
      .then((data) => active && setState({ loading: false, data }))
      .catch((err: unknown) => active && setState({ loading: false, error: (err as Error).message }));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { ...state, reload: () => setNonce((n) => n + 1) };
}
